/**
 * Calendar-date utilities. Invoice dates are stored as ISO "YYYY-MM-DD" strings without a
 * time component, which avoids timezone drift between the browser, the Worker and D1.
 */
export type ISODate = string;

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

export const DATE_FORMATS = [
  { id: 'DD MMM YYYY', example: '05 Oct 2026' },
  { id: 'MMM D, YYYY', example: 'Oct 5, 2026' },
  { id: 'MMMM D, YYYY', example: 'October 5, 2026' },
  { id: 'D MMMM YYYY', example: '5 October 2026' },
  { id: 'DD/MM/YYYY', example: '05/10/2026' },
  { id: 'MM/DD/YYYY', example: '10/05/2026' },
  { id: 'YYYY-MM-DD', example: '2026-10-05' },
  { id: 'DD.MM.YYYY', example: '05.10.2026' },
  { id: 'DD-MM-YYYY', example: '05-10-2026' },
] as const;

export type DateFormatId = (typeof DATE_FORMATS)[number]['id'];

export const DATE_FORMAT_IDS = DATE_FORMATS.map((f) => f.id) as DateFormatId[];

interface DateParts {
  year: number;
  month: number;
  day: number;
}

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export function parseISODate(value: string): DateParts | null {
  const match = ISO_DATE.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) return null;
  return { year, month, day };
}

export function isISODate(value: unknown): value is ISODate {
  return typeof value === 'string' && parseISODate(value) !== null;
}

function pad(value: number, length = 2): string {
  return String(value).padStart(length, '0');
}

export function toISODate(parts: DateParts): ISODate {
  return `${pad(parts.year, 4)}-${pad(parts.month)}-${pad(parts.day)}`;
}

function requireParts(value: ISODate): DateParts {
  const parts = parseISODate(value);
  if (!parts) throw new Error(`Invalid ISO date: ${value}`);
  return parts;
}

function toUTC(value: ISODate): Date {
  const { year, month, day } = requireParts(value);
  return new Date(Date.UTC(year, month - 1, day));
}

function fromUTC(date: Date): ISODate {
  return toISODate({ year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() });
}

/** Today's calendar date in the given IANA timezone. */
export function todayISO(timeZone = 'UTC', now: Date = new Date()): ISODate {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(now);
    const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
    return toISODate({ year: get('year'), month: get('month'), day: get('day') });
  } catch {
    return fromUTC(now);
  }
}

export function isValidTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
}

export function addDays(value: ISODate, days: number): ISODate {
  const date = toUTC(value);
  date.setUTCDate(date.getUTCDate() + days);
  return fromUTC(date);
}

/** Adds months, clamping to the last day of the target month (Jan 31 + 1 month = Feb 28/29). */
export function addMonths(value: ISODate, months: number, anchorDay?: number): ISODate {
  const { year, month, day } = requireParts(value);
  const total = year * 12 + (month - 1) + months;
  const targetYear = Math.floor(total / 12);
  const targetMonth = (total % 12) + 1;
  const targetDay = Math.min(anchorDay ?? day, daysInMonth(targetYear, targetMonth));
  return toISODate({ year: targetYear, month: targetMonth, day: targetDay });
}

export function addYears(value: ISODate, years: number): ISODate {
  return addMonths(value, years * 12);
}

/** Whole days from `from` to `to` (positive when `to` is later). */
export function diffDays(from: ISODate, to: ISODate): number {
  return Math.round((toUTC(to).getTime() - toUTC(from).getTime()) / 86_400_000);
}

export function compareISO(a: ISODate, b: ISODate): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

export function startOfMonth(value: ISODate): ISODate {
  const { year, month } = requireParts(value);
  return toISODate({ year, month, day: 1 });
}

export function endOfMonth(value: ISODate): ISODate {
  const { year, month } = requireParts(value);
  return toISODate({ year, month, day: daysInMonth(year, month) });
}

export function startOfYear(value: ISODate): ISODate {
  return `${value.slice(0, 4)}-01-01`;
}

export function endOfYear(value: ISODate): ISODate {
  return `${value.slice(0, 4)}-12-31`;
}

/** Monday-based start of week. */
export function startOfWeek(value: ISODate): ISODate {
  const date = toUTC(value);
  const weekday = (date.getUTCDay() + 6) % 7;
  return addDays(value, -weekday);
}

export function formatDate(value: ISODate | null | undefined, pattern: string = 'DD MMM YYYY'): string {
  if (!value) return '';
  const parts = parseISODate(value.slice(0, 10));
  if (!parts) return value;
  const monthName = MONTH_NAMES[parts.month - 1] ?? '';
  const tokens: Record<string, string> = {
    YYYY: pad(parts.year, 4),
    YY: pad(parts.year % 100),
    MMMM: monthName,
    MMM: monthName.slice(0, 3),
    MM: pad(parts.month),
    M: String(parts.month),
    DD: pad(parts.day),
    D: String(parts.day),
  };
  return pattern.replace(/YYYY|YY|MMMM|MMM|MM|M|DD|D/g, (token) => tokens[token] ?? token);
}

export function formatMonthLabel(yearMonth: string): string {
  const [year, month] = yearMonth.split('-');
  const name = MONTH_NAMES[Number(month) - 1] ?? '';
  return `${name.slice(0, 3)} ${year}`;
}

/** ISO timestamp helpers (UTC). */
export function nowISO(): string {
  return new Date().toISOString();
}

export function addSecondsISO(seconds: number, from: Date = new Date()): string {
  return new Date(from.getTime() + seconds * 1000).toISOString();
}

export function formatDateTime(isoTimestamp: string | null | undefined, timeZone = 'UTC'): string {
  if (!isoTimestamp) return '';
  const date = new Date(isoTimestamp);
  if (Number.isNaN(date.getTime())) return isoTimestamp;
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
  } catch {
    return date.toISOString();
  }
}

export function paymentTermsLabel(days: number | null | undefined): string {
  if (days === null || days === undefined) return '';
  if (days <= 0) return 'Due on receipt';
  return `Net ${days}`;
}
